import styled from "styled-components";
import { CommonCard } from "../atom";
import { AuthType } from "../constants/auth-type";
import PersonCertifyPassedIcon from "@/assets/icons/profile/person-certify-passed";
import EnterpriseCertifyPassedIcon from "@/assets/icons/profile/enterprise-certify-passed";
import EnterpriseCertifyFoxIcon from "@/assets/icons/profile/enterprise-certify-fox";
import FoxInvoiceIcon from "@/assets/icons/profile/fox-invoice-icon";
import StudentCertifyPassedIcon from "@/assets/icons/profile/student-certify-passed";
import StudentCertifyIcon from "@/assets/icons/profile/student-certify-normal";
import { Space } from "task-eva-ui";
import { useTranslation } from "react-i18next";

import SiteIcon from "@/assets/icons/profile/site";
import { CertifyStatus } from "../constants/certify-status";
import { Paragraph } from "task-eva-ui";
import RightArrow from "@/assets/icons/actions/right-arrow";
import PendingIcon from "@/assets/icons/common/pending";
import WarningIcon from "@/assets/icons/common/warning-circle";
import CheckedIcon from "@/assets/icons/common/checked-circle-icon";
import { useGlobalStore } from "@/store/GlobalStore";

interface IPersonInfo {
  realName?: string;
  certNumber?: string;
  authSuccessDate?: string;
}

interface IStudentInfo {
  realName?: string;
  schoolAddress?: string;
  schoolName?: string;
  timeAge?: number;
  educational?: string;
  startTime?: string;
  endTime?: string;
}

interface IEnterpriseInfo {
  company?: string;
  companySite?: string;
  vatNumber?: string;
}

export function InfoCard({
  type = AuthType.Person,
  handler = () => {},
  info,
  certifyStatus = CertifyStatus.NoCertified,
  isSubAccount = false,
  disabled = false,
}: {
  type?: AuthType;
  handler?: () => void;
  info?: IPersonInfo & IEnterpriseInfo & IStudentInfo;
  certifyStatus?: CertifyStatus;
  isSubAccount?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation("profile");
  const { isStudentValid, studentCertifyInfo } = useGlobalStore();

  const iconMap = new Map([
    [AuthType.Person, [<PersonCertifyPassedIcon />]],
    [AuthType.Enterprise, [<EnterpriseCertifyPassedIcon />]],
    [AuthType.Student, [<StudentCertifyPassedIcon />, <StudentCertifyIcon />]],
    [
      AuthType.FoxInvoiceInfo,
      [<EnterpriseCertifyFoxIcon />, <FoxInvoiceIcon />],
    ],
  ]);
  const gapMap = new Map([
    [AuthType.Person, -11],
    [AuthType.Student, 11],
    [AuthType.Enterprise, 11],
  ]);
  const personalChangeBtn = [
    CertifyStatus.PersonPassed,
    CertifyStatus.StudentFail,
    CertifyStatus.StudentExpire,
  ].includes(certifyStatus);

  return (
    <CommonCard
      size={
        [
          CertifyStatus.EnterprisePassed,
          CertifyStatus.EnterpriseChange,
          CertifyStatus.EnterpriseFox,
          CertifyStatus.EnterpriseChangeFailed,
        ].includes(certifyStatus)
          ? "enterprise-large"
          : "large"
      }
      className="info-card"
      onClick={handler}
      disabled={disabled}
    >
      <Space center gap={gapMap.get(type)} fitParent>
        {type === AuthType.Person && (
          <>
            <div
              className={
                certifyStatus === CertifyStatus.PersonalChanging
                  ? "pending tip"
                  : certifyStatus === CertifyStatus.PersonaChangeFailed
                  ? "tip"
                  : "checked tip"
              }
            >
              {certifyStatus === CertifyStatus.PersonalChanging ? (
                <>
                  <PendingIcon />
                  {t("certification_change_under_review")}
                </>
              ) : certifyStatus === CertifyStatus.PersonaChangeFailed ? (
                <>
                  <WarningIcon />
                  {t("certification_change_review_failed")}
                </>
              ) : (
                <>
                  <CheckedIcon />
                  {t("certification_verified")}
                </>
              )}
            </div>
            {personalChangeBtn &&
              certifyStatus !== CertifyStatus.PersonalChanging && (
                <div className="right more-right">
                  <RightArrow fill="#757879" />
                </div>
              )}
            <Space height="100%" gap={10}>
              {iconMap.get(type)![0]}
              <Space column gap={10} justify="center" height="100%">
                <InfoItem
                  label={t("certify_info_name")}
                  value={info?.realName}
                />
                <Space gap={36}>
                  <InfoItem
                    label={t("certify_info_number")}
                    value={info?.certNumber}
                  />
                  <InfoItem
                    label={t("certify_info_date")}
                    value={info?.authSuccessDate}
                  />
                </Space>
              </Space>
            </Space>
            <div className="right">
              <RightArrow fill="#757879" />
            </div>
          </>
        )}
        {type === AuthType.Student && (
          <>
            <div
              className={
                !isStudentValid && !studentCertifyInfo?.noStudentVerify
                  ? "expired tip"
                  : "checked tip"
              }
            >
              {!isStudentValid && !studentCertifyInfo?.noStudentVerify ? (
                <>
                  <WarningIcon />
                  {t("certification_expired")}
                </>
              ) : (
                `${t("certification_student_end_time")}${info?.endTime}`
              )}
            </div>
            <Space height="100%" gap={10}>
              <Space
                height="100%"
                align="center"
                padding={
                  !studentCertifyInfo?.noStudentVerify && !isStudentValid
                    ? "0 0 0 8px"
                    : "unset"
                }
              >
                {
                  iconMap.get(type)![
                    Number(
                      !studentCertifyInfo?.noStudentVerify && !isStudentValid
                    )
                  ]
                }
              </Space>
              <Space column gap={12} justify="center" height="100%">
                <InfoItem
                  label={t("certify_student_name")}
                  value={info?.realName}
                />
                <InfoItem
                  label={t("certify_student_timeAge")}
                  value={String(info?.timeAge)}
                />
              </Space>
              <Space
                column
                gap={12}
                justify="center"
                height="100%"
                padding="0 0 0 80px"
              >
                <InfoItem
                  label={t("certify_student_school")}
                  value={info?.schoolName}
                  needTip
                />
                <InfoItem
                  label={t("certify_student_address")}
                  value={info?.schoolAddress}
                  needTip
                />
              </Space>
            </Space>
          </>
        )}
        {type === AuthType.Enterprise && (
          <>
            <Space center>
              <div style={{ height: "110px" }}>{iconMap.get(type)![0]}</div>
              <Space column gap={14}>
                <MainText>{info?.company}</MainText>
                <Space gap={4} center>
                  <SiteIcon />
                  <SubText>{info?.companySite}</SubText>
                </Space>
              </Space>
            </Space>
            <div
              className={
                certifyStatus === CertifyStatus.EnterpriseChange
                  ? "pending tip"
                  : certifyStatus === CertifyStatus.EnterpriseFailed ||
                    certifyStatus === CertifyStatus.EnterpriseChangeFailed
                  ? "tip"
                  : "checked tip"
              }
            >
              {
                <>
                  {certifyStatus === CertifyStatus.EnterpriseChange && (
                    <>
                      <PendingIcon />
                      {t("certification_change_under_review")}
                    </>
                  )}
                  {(certifyStatus === CertifyStatus.EnterpriseFailed ||
                    certifyStatus === CertifyStatus.EnterpriseChangeFailed) && (
                    <>
                      <WarningIcon />
                      {t("certification_change_review_failed")}
                    </>
                  )}
                </>
              }
            </div>
          </>
        )}
        {type === AuthType.FoxInvoiceInfo && (
          <>
            <Space center justify={"flex-start"} width={"90%"}>
              {info?.company && info?.vatNumber && info?.companySite ? (
                <>
                  <div style={{ height: "110px" }}>{iconMap.get(type)![0]}</div>
                  <Space column gap={8}>
                    <FoxInfoText>
                      <div>{t("certification_fox_name")}</div>
                      {info?.company}
                    </FoxInfoText>
                    <FoxInfoText>
                      <div>{t("certification_fox_address")}</div>
                      {info?.companySite}
                    </FoxInfoText>
                    <FoxInfoText>
                      <div>{t("certification_fox_vat")}</div>
                      {info?.vatNumber}
                    </FoxInfoText>
                  </Space>
                </>
              ) : (
                <>
                  <div style={{ height: "100px" }}>{iconMap.get(type)![1]}</div>
                  <Space gap={14}>
                    <StepItem>
                      <div>
                        <span>{t("certification_step", { val: 1 })}</span>
                        <Line />
                      </div>
                      <p>{t("certification_step_text_1")}</p>
                    </StepItem>
                    <StepItem>
                      <div>
                        <span>{t("certification_step", { val: 2 })}</span>
                        <Line />
                      </div>
                      <p>{t("certification_step_text_2")}</p>
                    </StepItem>
                    <StepItem>
                      <div>
                        <span>{t("certification_step", { val: 3 })}</span>
                      </div>
                      <p>{t("certification_step_text_3")}</p>
                    </StepItem>
                  </Space>
                </>
              )}
            </Space>
            <div className="right">
              {!isSubAccount &&
                certifyStatus === CertifyStatus.EnterpriseFox &&
                t("modify_enterprise_info")}
              <RightArrow fill="#757879" />
            </div>
          </>
        )}
      </Space>
    </CommonCard>
  );
}

function InfoItem({
  label = "",
  value = "",
  valueSize = "14px",
  needTip = false,
}: {
  label?: string;
  value?: string;
  valueSize?: string;
  needTip?: boolean;
}) {
  return (
    <Space gap={5} center>
      <span style={{ color: "#9BA2B0", fontSize: "14px" }}>{label}</span>
      <span style={{ color: "#D9DFE0", fontSize: valueSize }}>
        {needTip ? (
          <Paragraph
            text={value}
            tooltip={value.length > 20}
            maxWidth="280px"
          />
        ) : (
          value
        )}
      </span>
    </Space>
  );
}

const MainText = styled.div`
  color: #e8f4f5;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
`;
const SubText = styled.div`
  color: #9ba2b0;
  font-size: 14px;
`;

const FoxInfoText = styled.div`
  color: #e8f4f5;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  div {
    display: inline-block;
    color: #9ba2b0;
    font-size: 14px;
    width: 60px;
    font-weight: 400;
  }
`;
const Line = styled.div`
  display: inline-block;
  width: 160px;
  height: 1px;
  margin: 0 10px;
  background-color: #404348;
`;
const StepItem = styled.div`
  span {
    padding: 2px 10px 3px;
    border-radius: 16px;
    background-color: #0d0f11;
    font-size: 12px;
    color: #9ba2b0;
    min-width: 56px;
    width: max-content;
  }
  > div {
    display: flex;
    align-items: center;
  }
  p {
    color: #e8f4f5;
    font-size: 14px;
    margin: 4px 0;
  }
`;
